import {
  Button,
  DropdownMenu,
  IconButton,
  ToggleButton,
} from '@wandb/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {FunctionComponent, useContext, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {toast} from 'react-toastify';

import {useCreateProjectMembersMutation} from '../generated/graphql';
import {PROJECT_MEMBERS_QUERY} from '../graphql/projects';
import {
  PROJECT_PAGE_QUERY,
  ProjectData,
} from '../state/graphql/projectPageQuery';
import {useViewer} from '../state/viewer/hooks';
import {
  EntityProjectPermissions,
  useViewerHasPermissionForEntityProject,
} from '../util/entityProjectPermissions';
import {
  doNotRetryContext,
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../util/errors';
import {getProjectAccess} from '../util/permissions';
import {useRampFlagStarredProjects} from '../util/rampFeatureFlags';
import {
  useTeamAdminIsNotRestrictedProjectMember,
  useViewerHasRestrictedProjectView,
} from '../util/restrictedProjectView';
import {StarredProjectsContext} from '../util/StarredProjectsContextProvider';
import * as urls from '../util/urls';
import {useNavUpdaterContext} from './NavContextProvider';
import {PROJECTS_BY_TEAM_QUERY} from './PaginatedList/projectQuery.query';
import {DeleteProjectModal} from './Project/DeleteProjectModal';
import {UndeleteRunsModal} from './UndeleteRunsModal';

export interface ExtraProjectActionsProps {
  project: ProjectData;
  isWeaveOnly?: boolean;
}

export const ExtraProjectActions: FunctionComponent<
  ExtraProjectActionsProps
> = ({project, isWeaveOnly}) => {
  const history = useHistory();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUndeleteModalOpen, setIsUndeleteModalOpen] = useState(false);

  const viewer = useViewer();
  const {toggleEditProjectDrawer} = useNavUpdaterContext();
  const isStarredProjectsEnabled = useRampFlagStarredProjects();
  const {
    starredProjects,
    onStarClick,
    refetch: refetchStarredProjects,
  } = useContext(StarredProjectsContext);
  const isStarred = starredProjects[project.id] !== undefined;

  const [createProjectMembersMutation] = useCreateProjectMembersMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
    refetchQueries: [
      // team admin has added themselves as a project member, so refetch the project data
      {
        query: PROJECTS_BY_TEAM_QUERY,
        variables: {entityName: project.entityName, first: 20},
      },
      {
        query: PROJECT_PAGE_QUERY,
        variables: {entityName: project.entityName, projectName: project.name},
      },
      {
        query: PROJECT_MEMBERS_QUERY,
        variables: {entityName: project.entityName, projectName: project.name},
      },
    ],
  });

  const projectAccess = getProjectAccess(project.access);

  const viewingRestricted = useViewerHasRestrictedProjectView({
    projectAccess,
    projectName: project.name,
    entityName: project.entityName,
  });

  const teamAdminIsNotRestrictedProjectMember =
    useTeamAdminIsNotRestrictedProjectMember({
      projectAccess,
      projectName: project.name,
      entityName: project.entityName,
    });

  const onJoinProjectClick = async () => {
    if (!viewer?.id) {
      return;
    }
    try {
      const res = await createProjectMembersMutation({
        variables: {
          userIds: [viewer.id],
          projectId: project.id,
        },
      });
      if (!res.data?.createProjectMembers?.success) {
        toast('Error joining project');
        return;
      }
    } catch (err) {
      const errMsg = extractErrorMessageFromApolloError(err);
      if (errMsg != null) {
        toast(`Error joining project: ${errMsg}`);
      } else {
        toast('Error joining project');
      }
    }
  };

  const {hasPermission: canDeleteProject} =
    useViewerHasPermissionForEntityProject(
      project.name,
      project.entityName,
      EntityProjectPermissions.DeleteProject
    );

  const {hasPermission: canUpdateProject} =
    useViewerHasPermissionForEntityProject(
      project.name,
      project.entityName,
      EntityProjectPermissions.UpdateProject
    );

  const {hasPermission: canRenameProject} =
    useViewerHasPermissionForEntityProject(
      project.name,
      project.entityName,
      EntityProjectPermissions.RenameProject
    );

  const viewerIsProjectOwner = viewer && project.user?.id === viewer?.id;
  const editEnabled =
    !viewingRestricted &&
    (canRenameProject ||
      canUpdateProject ||
      (!project.entity.isTeam && viewerIsProjectOwner));
  return (
    <Tailwind>
      <div className="flex items-center">
        {teamAdminIsNotRestrictedProjectMember && (
          <Button
            startIcon="add-new"
            variant="primary"
            onPress={() => onJoinProjectClick()}>
            Join project
          </Button>
        )}
        {editEnabled && (
          <Button
            isDisabled={!editEnabled}
            startIcon="pencil-edit"
            variant="ghost"
            onPress={toggleEditProjectDrawer}
            data-test="edit-project-details">
            Edit
          </Button>
        )}
        {isStarredProjectsEnabled && (
          <ToggleButton
            variant="star"
            aria-label={`Star project: ${project.name}`}
            isSelected={isStarred}
            onPress={() => {
              onStarClick(
                // Actual project data is not used, but createdAt is required so use lastActive as a placeholder
                {...project, createdAt: project.lastActive},
                !isStarred,
                'project overview'
              );
            }}
          />
        )}
        <DropdownMenu.Trigger>
          <IconButton
            isDisabled={viewingRestricted}
            icon="overflow-horizontal"
            variant="ghost"
            aria-label="dropdown-menu-icon-button"
          />
          <DropdownMenu.Content placement="bottom right">
            <DropdownMenu.Item
              isDisabled={!canDeleteProject}
              onAction={() => setIsDeleteModalOpen(true)}>
              <DropdownMenu.ItemDecorationLeft
                iconProps={{
                  icon: 'delete',
                }}
              />
              <DropdownMenu.ItemLabel>
                Delete project
                {!canDeleteProject && (
                  <span style={{fontStyle: 'italic'}}> (admin only)</span>
                )}
              </DropdownMenu.ItemLabel>
            </DropdownMenu.Item>
            {!isWeaveOnly && (
              <DropdownMenu.Item onAction={() => setIsUndeleteModalOpen(true)}>
                <DropdownMenu.ItemDecorationLeft
                  iconProps={{
                    icon: 'undo',
                  }}
                />
                <DropdownMenu.ItemLabel>
                  Undelete recently deleted runs
                </DropdownMenu.ItemLabel>
              </DropdownMenu.Item>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Trigger>
        <DeleteProjectModal
          isOpen={isDeleteModalOpen}
          project={project}
          setIsModalOpen={setIsDeleteModalOpen}
          handleModalClose={() => {
            setIsDeleteModalOpen(false);
            refetchStarredProjects();
            history.push(urls.allProjects(project.entityName));
          }}
        />
        <UndeleteRunsModal
          open={isUndeleteModalOpen}
          project={project}
          onClose={() => setIsUndeleteModalOpen(false)}
        />
      </div>
    </Tailwind>
  );
};
